import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { fmtDT } from "../security/ActivityTab";
import { SEVERITY_LABEL } from "./IncidentCreateDialog";
import { BreachClock } from "./BreachClock";

const STATUSES = ["open", "contained", "resolved", "closed"];
const FIELDS = ["status", "severity", "root_cause", "lessons_learned", "regulator_not_required_reason"];

export function IncidentDetailDialog({ incident, onClose, onSave, onNote }) {
  const [form, setForm] = useState({});
  const [note, setNote] = useState("");
  useEffect(() => { if (incident) setForm(Object.fromEntries(FIELDS.map((k) => [k, incident[k] || ""]))); }, [incident]);
  if (!incident) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const stamp = (k) => onSave({ [k]: new Date().toISOString() });

  return (
    <Dialog open={!!incident} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="incident-detail-dialog">
        <DialogHeader><DialogTitle>{incident.ref} · {incident.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600 whitespace-pre-wrap">{incident.description || "No description."}</p>
        <p className="text-xs text-slate-500">Detected {fmtDT(incident.detected_at)} · logged by {incident.reported_by}{incident.affected_orgs?.length ? ` · affects ${incident.affected_orgs.join(", ")}` : ""}</p>
        {incident.personal_data_breach && <BreachClock incident={incident} />}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="incident-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1"><Label>Severity</Label>
            <Select value={form.severity} onValueChange={(v) => set("severity", v)}><SelectTrigger data-testid="incident-detail-severity"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(SEVERITY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={!!incident.customers_notified_at} onClick={() => stamp("customers_notified_at")} data-testid="mark-customers-notified">
            {incident.customers_notified_at ? `Customers notified ${fmtDT(incident.customers_notified_at)}` : "Mark customers notified"}</Button>
          {incident.personal_data_breach && (
            <Button size="sm" variant="outline" disabled={!!incident.regulator_notified_at} onClick={() => stamp("regulator_notified_at")} data-testid="mark-regulator-notified">
              {incident.regulator_notified_at ? `Regulator notified ${fmtDT(incident.regulator_notified_at)}` : "Mark regulator notified"}</Button>
          )}
        </div>
        {incident.personal_data_breach && !incident.regulator_notified_at && (
          <div className="space-y-1"><Label>Reason regulator not notified (if not required)</Label><Input value={form.regulator_not_required_reason} onChange={(e) => set("regulator_not_required_reason", e.target.value)} data-testid="incident-no-notify-reason" /></div>
        )}
        <div className="space-y-1"><Label>Root cause</Label><Textarea rows={2} value={form.root_cause} onChange={(e) => set("root_cause", e.target.value)} data-testid="incident-root-cause" /></div>
        <div className="space-y-1"><Label>Lessons learned</Label><Textarea rows={2} value={form.lessons_learned} onChange={(e) => set("lessons_learned", e.target.value)} data-testid="incident-lessons" /></div>
        <div className="space-y-2">
          <Label>Timeline</Label>
          <div className="border rounded-sm divide-y max-h-48 overflow-y-auto" data-testid="incident-timeline">
            {(incident.timeline || []).map((t, i) => <div key={i} className="p-2 text-xs"><span className="text-slate-400">{fmtDT(t.at)} · {t.by}</span><p className="text-slate-700">{t.note}</p></div>)}
          </div>
          <div className="flex gap-2">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an update, e.g. action taken" data-testid="incident-note-input" />
            <Button variant="outline" disabled={!note.trim()} onClick={async () => { if (await onNote(note)) setNote(""); }} data-testid="incident-add-note-btn">Add</Button>
          </div>
        </div>
        <DialogFooter>
          <Badge variant="outline" className="mr-auto capitalize">{incident.status}</Badge>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => onSave(form)} data-testid="incident-save-btn">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
